import { Button, Modal, Spinner, useLoadingTimeout } from '@citizen-walk/shared-ui';
import { useEffect, useMemo, useState } from 'react';
import { useAnonymousAuth } from '../auth';
import { useMyIdentity } from '../auth';
import { useMembershipUserFields, useResolvedNickname } from '../auth';
import { useDistrictReady } from '../district';
import { useCurrentSession, useMembershipSessions } from '../sessions';
import { buildSurveyParticipantProfile } from './participantSnapshot';
import { SurveyForm } from './SurveyForm';
import { useMyResponseIds } from './useMyResponseIds';
import { useSurveyDetail } from './useSurveyDetail';
import { useSurveyResponse } from './useSurveyResponse';
import { useSurveys } from './useSurveys';

export interface SurveyListModalProps {
  open: boolean;
  onClose: () => void;
}

/**
 * アンケート一覧 + 回答 form Modal (Sprint T、user 第 16 弾、Phase 5)。
 *
 * 旧 SurveyListModal.tsx (L3 参照、docs/97 §3) のうち List + 個別 SurveyForm 起動 part を踏襲。
 *
 * 動作:
 *   - 一覧: useSurveys (summary subscribe) + useMyResponseIds (回答済 marker)
 *   - クリックで selectedId に切替、useSurveyDetail で full Survey 取得
 *   - useSurveyResponse で自分の既存 response 取得 (新規 / 編集を pre-fill)
 *   - SurveyForm submit 完了で一覧に戻る
 *
 * Phase 2 → Phase 5 差分:
 *   - 「Phase 5 で回答可」 marker を削除、クリックで SurveyForm 開く
 *   - 「✓ 回答済」 marker を追加 (useMyResponseIds の Set で判定)
 */
export function SurveyListModal({ open, onClose }: SurveyListModalProps) {
  // Sprint AM: 30s timeout 後の retry 用 key (UI2 無限スピナー解消)
  const [surveysRetryKey, setSurveysRetryKey] = useState(0);
  const district = useDistrictReady();
  // 地区フォールバック根絶: 解決済地区 district.id を明示 (旧 phase2-default 既定購読を撤去)。
  const surveysState = useSurveys(district.id, surveysRetryKey);
  const surveysLoadTimedOut = useLoadingTimeout(surveysState.status === 'loading');
  const auth = useAnonymousAuth();
  const uid = auth.status === 'authenticated' ? auth.user.uid : null;
  // 回答に記録する回答者情報 (旧 buildParticipantSnapshot 踏襲)。ニックネームは参加登録の値を
  // 使う (端末内の保存値だけだと、端末を移った人・登録画面で付けた人が「anon-…」になっていた)。
  const nickname = useResolvedNickname(uid, district.id);
  const membership = useMembershipUserFields(uid, district.id);
  const membershipSessions = useMembershipSessions(uid, district.id);
  const { state: sessionsState } = useCurrentSession(district.id);
  const participantProfile = useMemo(
    () =>
      buildSurveyParticipantProfile({
        builtin: membership.values.builtin,
        joinedSessionIds: membershipSessions.joinedSessionIds,
        sessions: sessionsState.items,
      }),
    [membership.values.builtin, membershipSessions.joinedSessionIds, sessionsState.items],
  );
  // R-70 P3b: 端末引き継ぎ後も回答済みを横断判定するための現UID+引き継ぎ元UID群。
  const identity = useMyIdentity(uid, district.id);

  const [selectedId, setSelectedId] = useState<string | null>(null);

  const surveyIds = useMemo(
    () => (surveysState.status === 'ready' ? surveysState.items.map((s) => s.id) : []),
    [surveysState],
  );
  const myResponses = useMyResponseIds(district.id, surveyIds, uid, identity.allUids);

  // selected survey の詳細 + 自分の既存 response
  const detail = useSurveyDetail(district.id, selectedId);
  const myResp = useSurveyResponse(district.id, selectedId, uid, identity.allUids);
  // form を出してよい survey (= 自分の回答の読込が終わった survey)。
  const [formReadyFor, setFormReadyFor] = useState<string | null>(null);
  const respLoaded = myResp.state.status === 'ready';
  useEffect(() => {
    if (selectedId === null) setFormReadyFor(null);
    else if (respLoaded) setFormReadyFor((prev) => (prev === selectedId ? prev : selectedId));
  }, [selectedId, respLoaded]);

  if (!open) return null;

  function handleSelect(id: string) {
    setSelectedId(id);
  }

  function handleBack() {
    setSelectedId(null);
  }

  function handleClose() {
    setSelectedId(null);
    onClose();
  }

  // ----- 詳細 (回答 form) 表示 -----
  if (selectedId !== null) {
    return (
      <Modal
        open={open}
        onClose={handleClose}
        title="📋 アンケート回答"
        size="lg"
        closeOnOverlayClick={false}
      >
        <div className="mb-3">
          <button
            type="button"
            onClick={handleBack}
            className="text-xs text-primary-600 hover:text-primary-700 underline bg-transparent border-none p-0 cursor-pointer"
          >
            ← 一覧に戻る
          </button>
        </div>

        {detail.status === 'loading' && <p className="text-slate-500 text-center py-6">読込中…</p>}
        {detail.status === 'error' && (
          <p className="text-error-700 text-center py-6">取得エラー: {detail.error.message}</p>
        )}
        {detail.status === 'not-found' && (
          <p className="text-slate-500 text-center py-6">アンケートが見つかりません。</p>
        )}
        {detail.status === 'ready' && !uid && (
          <p className="text-warning-700 text-center py-6">
            認証情報が確定していないため回答できません。
          </p>
        )}
        {/* 自分の回答を読み終わるまで form を出さない。form は初回描画の回答で入力欄を作るため、
            先に出すと「更新する」なのに入力欄が空のままになる。一度出した後は (送信失敗で error に
            なっても) 出し続け、入力途中の内容を消さない。 */}
        {detail.status === 'ready' &&
          uid &&
          formReadyFor !== selectedId &&
          (myResp.state.status === 'error' ? (
            <p className="text-error-700 text-center py-6">
              回答の読み込みに失敗しました。通信状態を確認して、開き直してください。
            </p>
          ) : (
            <p className="text-slate-500 text-center py-6">読込中…</p>
          ))}
        {detail.status === 'ready' && uid && formReadyFor === selectedId && (
          <SurveyForm
            key={detail.survey.id}
            survey={detail.survey}
            existingResponse={myResp.state.status === 'error' ? null : myResp.state.response}
            uid={uid}
            districtId={district.id}
            participantNickname={nickname || `anon-${uid.slice(0, 6)}`}
            participantProfile={participantProfile}
            onSubmitted={() => setSelectedId(null)}
            onCancel={handleBack}
          />
        )}
      </Modal>
    );
  }

  // ----- 一覧表示 -----
  return (
    <Modal open={open} onClose={handleClose} title="📋 アンケート" size="md">
      {/* Sprint AM: 30s timeout 後 retry banner (UI2 無限スピナー解消) */}
      {surveysState.status === 'loading' && !surveysLoadTimedOut && (
        <div className="text-center py-6">
          <Spinner label="読込中…" />
        </div>
      )}
      {surveysState.status === 'loading' && surveysLoadTimedOut && (
        <div className="text-center py-6 space-y-2">
          <p className="text-sm text-warning-700">読込が遅れています。再試行してください。</p>
          <Button variant="secondary" size="sm" onClick={() => setSurveysRetryKey((k) => k + 1)}>
            再試行
          </Button>
        </div>
      )}
      {surveysState.status === 'error' && (
        <div className="text-center py-6 space-y-2">
          <p className="text-error-700">取得エラー: {surveysState.error.message}</p>
          <Button variant="secondary" size="sm" onClick={() => setSurveysRetryKey((k) => k + 1)}>
            再試行
          </Button>
        </div>
      )}
      {surveysState.status === 'ready' && surveysState.items.length === 0 && (
        <p className="text-slate-500 text-center py-6">現在公開中のアンケートはありません。</p>
      )}
      {surveysState.status === 'ready' && surveysState.items.length > 0 && (
        <ul className="grid gap-2">
          {surveysState.items.map((s) => {
            const responded = myResponses.ids.has(s.id);
            return (
              <li key={s.id}>
                <button
                  type="button"
                  onClick={() => handleSelect(s.id)}
                  className="w-full text-left p-3 border border-slate-200 rounded-lg bg-white hover:bg-slate-50 cursor-pointer transition-colors"
                >
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-medium flex-1 text-slate-900">{s.title || '(無題)'}</span>
                    {responded ? (
                      <span className="text-xs bg-green-100 text-green-800 px-2 py-0.5 rounded">
                        ✓ 回答済
                      </span>
                    ) : (
                      <span className="text-xs bg-primary-100 text-primary-700 px-2 py-0.5 rounded">
                        回答する
                      </span>
                    )}
                  </div>
                  {s.description && (
                    <p className="text-sm text-slate-600 line-clamp-2">{s.description}</p>
                  )}
                  <p className="text-xs text-slate-400 mt-1">
                    質問 {s.questionCount} 問{s.allowEdit && '（編集可）'}
                  </p>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </Modal>
  );
}
